import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
    { label: "Services", id: "services" },
    { label: "Portfolio", id: "portfolio" },
    { label: "Service Area", id: "area" },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 150 : 0);
    };

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "border-b border-line bg-bone/90 backdrop-blur-md"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <button
                    data-testid="nav-brand-logo"
                    onClick={() => go("top")}
                    aria-label="Back to top"
                    className="transition-opacity hover:opacity-80"
                >
                    <Wordmark />
                </button>

                <nav className="hidden items-center gap-9 md:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="group relative font-mono text-[11px] uppercase tracking-[0.2em] text-clay transition-colors hover:text-espresso"
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-oak transition-all duration-300 group-hover:w-full" />
                        </button>
                    ))}
                    <motion.button
                        data-testid="nav-inquiry-cta"
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => go("inquiry")}
                        className="bg-espresso px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-bone transition-colors hover:bg-oak"
                    >
                        Request Estimate
                    </motion.button>
                </nav>

                <button
                    data-testid="nav-mobile-toggle"
                    className="flex h-11 w-11 items-center justify-center text-espresso md:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Close menu" : "Open menu"}
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="border-b border-line bg-bone/95 backdrop-blur-md md:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 py-6">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`nav-mobile-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="py-3 text-left font-serif text-2xl text-espresso"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="nav-mobile-inquiry-cta"
                                onClick={() => go("inquiry")}
                                className="mt-4 bg-espresso px-6 py-4 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-bone"
                            >
                                Request Estimate
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
